"use client";

import { cn } from "@dafiti/closet-ui/utils";
import { useTranslations } from "next-intl";
import { ReactNode, useEffect, useRef } from "react";

import { useDropdownDismiss } from "../../../hooks/useDropdownDismiss.hook";
import { QuickFilterFooter } from "./QuickFilterFooter";

export type QuickFilterBoxAlign = "start" | "center";

interface QuickFilterDesktopBoxProps {
  label: string;
  containerId: string;
  onClear: () => void;
  onApply: () => void;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  align?: QuickFilterBoxAlign;
  children?: ReactNode;
}

export function QuickFilterDesktopBox({
  label,
  containerId,
  onClear,
  onApply,
  onClose,
  triggerRef,
  align = "center",
  children,
}: QuickFilterDesktopBoxProps) {
  const t = useTranslations("catalog");
  const boxRef = useRef<HTMLDivElement>(null);

  const { maxHeight, placement = "bottom" } =
    useDropdownDismiss({ boxRef, triggerRef, onClose }) ?? {};

  useEffect(() => {
    if (boxRef.current) {
      const firstFocusable = boxRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }
  }, []);

  return (
    <div
      ref={boxRef}
      id={containerId}
      role="dialog"
      aria-label={t("drawer.facetDialog", { label })}
      className={cn(
        "absolute z-50 flex w-[320px] flex-col animate-in fade-in duration-150",
        align === "center" ? "left-1/2 -translate-x-1/2" : "left-0",
        placement === "bottom"
          ? "top-full mt-4 slide-in-from-top-2"
          : "bottom-full mb-4 slide-in-from-bottom-2",
      )}
      style={{ maxHeight: maxHeight ? `${maxHeight}px` : undefined }}
      data-testid={`quick-filter-box-${containerId}`}
    >
      {children}

      <QuickFilterFooter onClear={onClear} onApply={onApply} />
    </div>
  );
}
